import React from 'react';
import { withRouter } from 'react-router-dom';
import {
  Card,
  CardContent,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';

import Campaign from './cards/Campaign.jsx';

import { campaignActions } from '../../actions/campaign.js';
import { connect } from 'react-redux';

import NoCampaign from '../ux/NoCampaign';
import AccountChecklist from './AccountChecklist';

const ACCENT = '#111936';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live' },
  { key: 'completed', label: 'Completed' },
];

const styles = () => ({
  page: {
    padding: '28px 16px 80px',
  },
  title: {
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 22,
    color: '#111827',
  },
  subtitle: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 13.5,
    color: '#6B7280',
    marginTop: 4,
  },
  toolbar: {
    marginTop: 20,
    marginBottom: 18,
  },
  filterPill: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    borderRadius: 999,
    padding: '6px 16px',
    border: '1px solid #E5E7EB',
    background: 'white',
    color: '#4B5563',
    cursor: 'pointer',
    marginRight: 8,
    transition: 'all 120ms ease',
    '&:hover': { borderColor: ACCENT, color: ACCENT },
  },
  filterPillActive: {
    background: ACCENT,
    borderColor: ACCENT,
    color: 'white',
    '&:hover': { color: 'white' },
  },
  search: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      background: 'white',
    },
  },
  emptyCard: {
    borderRadius: 12,
    border: '1px dashed #D1D5DB',
    boxShadow: 'none',
    textAlign: 'center',
  },
  emptyText: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 14,
    color: '#6B7280',
  },
  clearLink: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    color: ACCENT,
    cursor: 'pointer',
    marginTop: 6,
    display: 'inline-block',
  },
});

function matchesFilter(campaign, filter) {
  if (filter === 'live') return Boolean(campaign.is_live);
  if (filter === 'completed') return campaign.status === 'completed';
  return true;
}

function matchesSearch(campaign, query) {
  if (!query) return true;
  const haystack = `${campaign.name || ''} ${campaign.brand_name || ''} ${
    campaign.objective || ''
  }`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

function CardSkeleton() {
  return (
    <Card
      elevation={0}
      style={{
        marginBottom: 16,
        borderRadius: 12,
        border: '1px solid #E5E7EB',
      }}
    >
      <CardContent style={{ padding: '18px 20px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Skeleton variant="rect" width={44} height={44} style={{ borderRadius: 10 }} />
          </Grid>
          <Grid item xs>
            <Skeleton width="45%" height={22} />
            <Skeleton width="30%" height={16} />
          </Grid>
          <Grid item>
            <Skeleton variant="rect" width={76} height={26} style={{ borderRadius: 999 }} />
          </Grid>
        </Grid>
        <Skeleton width="90%" style={{ marginTop: 14 }} />
        <Grid container spacing={2} style={{ marginTop: 10 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={3} key={i}>
              <Skeleton width="70%" height={20} />
              <Skeleton width="50%" height={14} />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
}

class CampaignDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: 'all',
      query: '',
    };
  }

  componentDidMount() {
    this.props.getInfluencerCampaigns();
  }

  render() {
    const { classes, campaigns, loading } = this.props;
    const { filter, query } = this.state;

    const all = campaigns || [];
    const counts = {
      all: all.length,
      live: all.filter((c) => matchesFilter(c, 'live')).length,
      completed: all.filter((c) => matchesFilter(c, 'completed')).length,
    };
    const visible = all.filter((c) => matchesFilter(c, filter) && matchesSearch(c, query));
    const hasFiltersApplied = filter !== 'all' || query !== '';

    return (
      <div className={`${classes.page} inf-shell`}>
        <div className="inf-shell__inner">
          {/* The outstanding action comes first, before the hero: it is what
              stops a creator getting paid, so it should not be something they
              scroll to find. */}
          <AccountChecklist />

          <div className="inf-hero">
            <div
              className="inf-hero-inner"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                gap: 16,
              }}
            >
              <div>
                <p className="inf-hero-kicker">Creator Workspace</p>
                <h1 className="inf-hero-title">Your Campaigns</h1>
                <p className="inf-hero-sub">
                  Track every campaign you are part of, from going live to getting paid.
                </p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div className="inf-stat">
                  <div className="inf-stat-num">{counts.all}</div>
                  <div className="inf-stat-label">Total</div>
                </div>
                <div className="inf-stat">
                  <div className="inf-stat-num">{counts.live}</div>
                  <div className="inf-stat-label">Live</div>
                </div>
                <div className="inf-stat">
                  <div className="inf-stat-num">{counts.completed}</div>
                  <div className="inf-stat-label">Completed</div>
                </div>
              </div>
            </div>
          </div>

          {/* Wide screens get a SaaS two-column split: campaigns carry the
              main column, setup rides the right rail where it stays in view
              without pushing the list down. Below 1080px it stacks, and the
              rail becomes a band above the list. */}
          <Grid
            container
            className={classes.toolbar}
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Grid item>
              {FILTERS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={`${classes.filterPill} ${
                    filter === key ? classes.filterPillActive : ''
                  }`}
                  onClick={() => this.setState({ filter: key })}
                >
                  {label}
                  {counts[key] ? ` (${counts[key]})` : ''}
                </button>
              ))}
            </Grid>
            <Grid item xs={12} sm="auto">
              <TextField
                className={classes.search}
                variant="outlined"
                size="small"
                placeholder="Search campaigns"
                value={query}
                onChange={(event) => this.setState({ query: event.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 18, color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {loading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : all.length === 0 ? (
            <NoCampaign />
          ) : visible.length === 0 ? (
            <Card className={classes.emptyCard}>
              <CardContent style={{ padding: '36px 20px' }}>
                <Typography className={classes.emptyText}>
                  No campaigns match
                  {query ? ` “${query}”` : ''}
                  {filter !== 'all' ? ` under “${FILTERS.find((f) => f.key === filter).label}”` : ''}.
                </Typography>
                {hasFiltersApplied ? (
                  <span
                    className={classes.clearLink}
                    role="button"
                    tabIndex={0}
                    onClick={() => this.setState({ filter: 'all', query: '' })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') this.setState({ filter: 'all', query: '' });
                    }}
                  >
                    Clear filters
                  </span>
                ) : null}
              </CardContent>
            </Card>
          ) : (
            <div className="inf-cards">
              {visible.map((campaign) => (
                <Campaign {...campaign} key={campaign.id} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loading: state.campaign.loading,
  campaigns: state.campaign.campaigns,
});

const actionCreators = {
  getInfluencerCampaigns: campaignActions.getInfluencerCampaigns,
};

export default connect(
  mapStateToProps,
  actionCreators,
)(withRouter(withStyles(styles)(CampaignDashboard)));
